import React, { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import styled from 'styled-components';
import {
  H1,
  paletteHelper,
  typographyHelper,
  spacingHelper,
  Button,
  Inline,
  P,
} from '@construct-kit/core';
import styles from './SearchArea.scss';
import type { AllChannelObjects } from '../../../shared/model/tsTypes';
import { breakpoints, layout } from '../../../../styles/_variables';
import history from '../../../client/service/history';
import { ChannelSwitcher } from '../../../shared/components/ChannelSwitcher';
import { SmallScreenLocationSuggestDialog } from '../../../shared/components/SmallScreenLocationSuggestDialog';
import {
  DEFAULT_FILTERS_REFINEMENTS_OPTIONS,
  mapFilterOptionsToRefinements,
} from '../../searchResult/refinements/components/mapRefinementsFilters';
import {
  urlFromRefinements,
  agencyUrlFromRefinements,
} from '../../searchResult/refinements/services/urlFromRefinements';
import { resetPageInRefinements } from '../../searchResult/refinements/FilterRefinements';
import { saveRecentSearch } from '../../../client/service/preferenceService';
import { LocationSearchBar } from '../../../shared/components/LocationSearchBar';
import RCALogo from './RCALogo';
import { FilterModal } from './FilterModal';
import type {
  ExtendedLocationSuggestOption,
  ExtendedMultiLocationSuggestOption,
  SelectedFiltersOptionsType,
} from '../../../shared/model/constants/filterTypes';
import PropertyTypesDropdownFilter from './PropertyTypesDropdownFilter';
import { trackSearchClick } from '../tracking/homepageTealium';
import { trackSearchRefinementModalOpen } from '../../../shared/tracking/tealium/tracking';
import ModalWithScrollLock from './SmallScreenSearchModal';
import {
  setRecentLocations,
  getRecentLocations,
  getFirstLocationState,
} from '../../recentSearchesService';
import homepageUrlGenerator from '../../../shared/model/transformers/homepageUrlGenerator';
import { getBackgroundImageFromId } from '../util/getBackgroundImageFromId';
import { customerChannelsArray } from '../../../shared/model/constants/customerChannels';
import {
  getCustomerMostRecentLocation,
  setCustomerRecentLocations,
} from '../../../shared/customerRecentSearches';
import { CUSTOMER_AGENCY, PAGE_TYPE_HOME_PAGE } from '../../../shared/tracking/tealium/constants';
import { setCookie } from '../../../client/cookie/cookieService';

export type HomepageSearchAreaProps = {
  heading: string;
  currentChannel: AllChannelObjects;
  channels: Array<AllChannelObjects>;
  onSetChannelTracking: ({
    channelObject,
    homepageType,
  }: {
    channelObject: AllChannelObjects;
    homepageType: string;
  }) => void;
};

const BackgroundWrapper = styled.div`
  height: 17.5rem;
  max-height: 17.5rem;
  position: relative;
  background-color: variables.$color-bluestone;

  img {
    border-radius: 0;
  }

  ${({ theme }) => theme.layoutBreakpoints.large} {
    height: 27.5rem;
    max-height: 27.5rem;
  }

  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    margin: 0 auto;
    max-width: ${layout.maxWidthDesktopExtraWide};

    picture {
      margin: 0 ${spacingHelper('medium')};
    }

    img {
      border-radius: ${({ theme }) => theme.border.radius.large};
    }
  }
`;

const HeroContentWrapper = styled.div`
  position: relative;
  top: ${spacingHelper('medium')};

  ${({ theme }) => theme.layoutBreakpoints.large} {
    top: ${spacingHelper('extraLarge')};
  }
`;

const Heading = styled(H1)`
  color: ${paletteHelper('textInvertedStatic')};
  ${typographyHelper('title03')};
  text-align: center;
  margin: 0 ${spacingHelper('medium medium medium')};

  ${({ theme }) => theme.layoutBreakpoints.large} {
    margin-bottom: 1.25rem;
    ${typographyHelper('title01')};
  }
`;

const LocationSearchWrapper = styled.div`
  background-color: ${paletteHelper('backgroundPrimary')};
  margin: auto ${spacingHelper('medium')};
  height: 3rem;
  border-radius: 1.5rem;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.16);

  ${({ theme }) => theme.layoutBreakpoints.large} {
    margin: auto;
    max-width: 46rem;
    height: 8rem;
    border-radius: ${({ theme }) => theme.border.radius.large};
  }
`;

const StyledSearchButton = styled(Button)`
  border-radius: ${spacingHelper('large')};
`;

const ButtonContainer = styled(Inline)`
  position: absolute;
  z-index: 90;
  right: ${spacingHelper('large')};
`;

const StyledSearchContainer = styled(Inline)`
  position: relative;
  padding-right: 12rem;
  margin: ${spacingHelper('medium 0 medium medium')};
`;

const StyledStartSearchButton = styled(Button)`
  padding: 0;
  > *:first-child {
    position: absolute;
    left: 0.5rem;
  }

  :hover {
    background-color: transparent;
  }
`;

const LargeScreenWrapper = styled.div`
  display: none;

  @media (min-width: ${breakpoints.breakpointSmMd}) {
    display: unset;
  }
`;

const SmallScreenWrapper = styled.div`
  @media (min-width: ${breakpoints.breakpointSmMd}) {
    display: none;
  }
`;

const HomepageSearchArea = ({
  currentChannel,
  heading,
  channels,
  onSetChannelTracking,
}: HomepageSearchAreaProps): ReactElement => {
  const [shouldOpenLocationDialog, setShouldOpenLocationDialog] = useState<boolean>(false);
  const [selectedFilterOptionsHomepage, setSelectedFilterOptionsHomepage] =
    useState<SelectedFiltersOptionsType>({
      ...DEFAULT_FILTERS_REFINEMENTS_OPTIONS,
      channel: currentChannel,
      searchLocations: [],
    });

  const customerLocationSuggestRef = useRef<HTMLInputElement>(null);

  const isCustomerHomepage = customerChannelsArray.includes(currentChannel);

  const selectedSearchLocationIsEmpty =
    !selectedFilterOptionsHomepage.searchLocations ||
    selectedFilterOptionsHomepage.searchLocations?.length === 0;

  const populateSearchLocations = ():
    | ExtendedMultiLocationSuggestOption[]
    | ExtendedLocationSuggestOption[]
    | undefined => {
    if (selectedSearchLocationIsEmpty) {
      return !isCustomerHomepage ? getRecentLocations() : getCustomerMostRecentLocation();
    }
    return selectedFilterOptionsHomepage.searchLocations;
  };

  const initialFilterOptions = {
    ...DEFAULT_FILTERS_REFINEMENTS_OPTIONS,
    channel: currentChannel,
    searchLocations: selectedFilterOptionsHomepage.searchLocations,
  };

  const setSelectedChannel = (channel: AllChannelObjects) => {
    const url = homepageUrlGenerator(channel, window.location.pathname);
    onSetChannelTracking({ channelObject: currentChannel, homepageType: PAGE_TYPE_HOME_PAGE });
    history.push(url);
    setSelectedFilterOptionsHomepage({ ...selectedFilterOptionsHomepage, channel });
  };

  const handleSearchOnClick = () => {
    const filterOptionsToRefinements = mapFilterOptionsToRefinements(selectedFilterOptionsHomepage);
    const resetPageRefinements = resetPageInRefinements(filterOptionsToRefinements);

    if (!isCustomerHomepage) {
      const newSearchUrl = urlFromRefinements(resetPageRefinements);
      setRecentLocations(selectedFilterOptionsHomepage.searchLocations);
      saveRecentSearch(newSearchUrl);
      setCookie('ads_params_state', getFirstLocationState(), { expires: 30 });
      history.push(newSearchUrl);
    } else {
      const newSearchUrl = agencyUrlFromRefinements(resetPageRefinements);
      setCustomerRecentLocations(
        (selectedFilterOptionsHomepage.searchLocations as ExtendedLocationSuggestOption[])[0],
      );
      const searchParams = newSearchUrl.search !== '' ? `?${newSearchUrl.search}` : '';
      window.location.assign(`${newSearchUrl.pathname}${searchParams}`);
    }
  };

  const onLocationSearchBoxClick = () => {
    trackSearchRefinementModalOpen({
      searchMode: 'search',
      userAction: 'location-field',
    });
  };

  const trackAndExecuteSearch = (trackingSource: string) => {
    if (isCustomerHomepage && selectedSearchLocationIsEmpty) {
      customerLocationSuggestRef.current?.focus();
      onLocationSearchBoxClick();
      return;
    }

    const homepageType = isCustomerHomepage
      ? `${CUSTOMER_AGENCY}:${PAGE_TYPE_HOME_PAGE}`
      : PAGE_TYPE_HOME_PAGE;
    trackSearchClick({
      channelObject: currentChannel,
      homepageType,
      trackingSource,
    });

    handleSearchOnClick();
  };

  const onSmallScreenLocationSearchBoxClick = () => {
    onLocationSearchBoxClick();
    setShouldOpenLocationDialog(true);
  };

  useEffect(() => {
    if (
      !selectedFilterOptionsHomepage.searchLocations ||
      selectedFilterOptionsHomepage.searchLocations.length === 0
    ) {
      setSelectedFilterOptionsHomepage({
        ...selectedFilterOptionsHomepage,
        searchLocations: populateSearchLocations(),
      });
      setCookie('ads_params_state', getFirstLocationState(), { expires: 30 });
    }
  }, []);

  const backgroundImageId = getBackgroundImageFromId(currentChannel.id);

  return (
    <BackgroundWrapper>
      <picture className={styles.hero}>
        <source
          media="(min-width: 768px)"
          srcSet={`/listing-ui-assets/img/homepage/hero-img-${backgroundImageId}-large.webp?version=2 1920w`}
          type="image/webp"
        />
        <source
          srcSet={`/listing-ui-assets/img/homepage/hero-img-${backgroundImageId}-small.webp?version=2 750w`}
          type="image/webp"
        />
        <img
          className={styles.heroImage}
          alt="alt text"
          srcSet={`
              /listing-ui-assets/img/homepage/hero-img-${backgroundImageId}-small.jpg?version=2 750w,
              /listing-ui-assets/img/homepage/hero-img-${backgroundImageId}-large.jpg?version=2 1920w
            `}
          sizes="750w, (min-width: 768px) 1920px"
          src={`/listing-ui-assets/img/homepage/hero-img-${backgroundImageId}-small.jpg?version=2 750w`}
        />
      </picture>
      <HeroContentWrapper>
        <Heading>{heading}</Heading>
        <LocationSearchWrapper>
          <LargeScreenWrapper>
            <ChannelSwitcher
              channels={channels}
              setSelectedChannel={setSelectedChannel}
              defaultChannel={currentChannel}
            />
            <StyledSearchContainer gap="medium" grow={false}>
              {!isCustomerHomepage && (
                <PropertyTypesDropdownFilter
                  selectedFilterOptions={selectedFilterOptionsHomepage}
                  setSelectedFilterOptionsHomepage={setSelectedFilterOptionsHomepage}
                />
              )}
              <LocationSearchBar
                isFromHomePage
                selectedFiltersOptions={selectedFilterOptionsHomepage}
                initialFilterOptions={initialFilterOptions}
                setSelectedFiltersOptions={setSelectedFilterOptionsHomepage}
                handleSearchOnClick={handleSearchOnClick}
                onLocationSearchBoxClick={onLocationSearchBoxClick}
                customerLocationSuggestRef={customerLocationSuggestRef}
                isForSingleLocation={isCustomerHomepage}
              />
              <ButtonContainer gap="extraSmall" grow={false}>
                <FilterModal
                  initialFilterOptions={initialFilterOptions}
                  selectedFilterOptionsHomepage={selectedFilterOptionsHomepage}
                  setSelectedFilterOptionsHomepage={setSelectedFilterOptionsHomepage}
                  handleSearchOnClick={() => trackAndExecuteSearch('filters')}
                  disabled={isCustomerHomepage && selectedSearchLocationIsEmpty}
                />
                <StyledSearchButton
                  sizeVariant="medium"
                  aria-label="search"
                  onClick={() => trackAndExecuteSearch('main')}
                >
                  Search
                </StyledSearchButton>
              </ButtonContainer>
            </StyledSearchContainer>
          </LargeScreenWrapper>
          <SmallScreenWrapper>
            <StyledStartSearchButton
              icon={<RCALogo />}
              variant="naked"
              fullWidth
              aria-label="start location search"
              onClick={() => onSmallScreenLocationSearchBoxClick()}
            >
              <P variant="subtitle01">Start a search</P>
            </StyledStartSearchButton>
            {shouldOpenLocationDialog && (
              <ModalWithScrollLock
                opened={shouldOpenLocationDialog}
                onRequestClose={() => setShouldOpenLocationDialog(false)}
                getAppElement={() => document.getElementById('wrapper') ?? ''}
              >
                <SmallScreenLocationSuggestDialog
                  onClose={() => {
                    setShouldOpenLocationDialog(false);
                  }}
                  selectedFiltersOptions={selectedFilterOptionsHomepage}
                  initialFilterOptions={initialFilterOptions}
                  setSelectedFiltersOptions={setSelectedFilterOptionsHomepage}
                  handleSearchOnClick={() => trackAndExecuteSearch('mobile')}
                  isForSingleLocation={isCustomerHomepage}
                  isIncludingFilterDialog
                />
              </ModalWithScrollLock>
            )}
          </SmallScreenWrapper>
        </LocationSearchWrapper>
      </HeroContentWrapper>
    </BackgroundWrapper>
  );
};

export default HomepageSearchArea;
